// Agent runs of a project (decision D28, plan §3): GET /v1/agent-runs?project_id, newest first, as a table — agent,
// purpose (run / consult / delegate / review / reflect / assistant), parent run, outcome, steps, model and tool calls,
// tokens, cost, started and finished. A consult or delegate child run is listed right under its parent run, indented,
// so one task's whole agent tree reads top-down. Unknown cost is shown as unknown, never as zero (RF-OBS-03).
import { useEffect, useMemo, useState } from "react";
import type { AgentRun, AgentRunOutcome, AgentRunPurpose } from "@maestros/contracts";
import { AGENT_RUN_OUTCOMES } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import type { MessageKey } from "../../i18n/en.ts";
import { listAgentRuns } from "../../api/endpoints-microsystem.ts";
import { listAgents } from "../../api/endpoints-agents.ts";
import { formatCents, formatNumber } from "../../lib/format.ts";
import { useDebouncedCallback } from "../../lib/useAsync.ts";
import { useRealtimeEvents } from "../../realtime/RealtimeProvider.tsx";
import { Button } from "../../components/Button.tsx";
import { SelectField } from "../../components/Field.tsx";
import { EmptyState, ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { Pill } from "../../components/Status.tsx";
import { CpuIcon, RefreshIcon } from "../../components/icons.tsx";
import type { IconKind, Tone } from "../../lib/status.ts";

export const AGENT_RUNS_LIMIT = 200;

const OUTCOME_VIEW: Record<AgentRunOutcome, { tone: Tone; icon: IconKind }> = {
  running: { tone: "info", icon: "activity" },
  succeeded: { tone: "success", icon: "check" },
  failed: { tone: "danger", icon: "x" },
  interrupted: { tone: "warning", icon: "pause" },
  blocked: { tone: "warning", icon: "ban" },
  cancelled: { tone: "neutral", icon: "ban" },
};

export interface RunRow {
  run: AgentRun;
  depth: number;
}

/**
 * Orders runs as a tree: every root (no parent, or a parent outside the loaded page) newest first, each followed by its
 * children oldest first (the order they were consulted/delegated), recursively. A cycle can never loop: each run is
 * placed once.
 */
export function nestRuns(runs: readonly AgentRun[]): RunRow[] {
  const ids = new Set(runs.map((r) => r.id));
  const children = new Map<string, AgentRun[]>();
  const roots: AgentRun[] = [];
  for (const r of runs) {
    if (r.parent_run_id !== null && ids.has(r.parent_run_id) && r.parent_run_id !== r.id) {
      const list = children.get(r.parent_run_id) ?? [];
      list.push(r);
      children.set(r.parent_run_id, list);
    } else roots.push(r);
  }
  const byStartDesc = (a: AgentRun, b: AgentRun) => b.started_at.localeCompare(a.started_at);
  const out: RunRow[] = [];
  const placed = new Set<string>();
  const visit = (r: AgentRun, depth: number) => {
    if (placed.has(r.id)) return;
    placed.add(r.id);
    out.push({ run: r, depth });
    for (const c of [...(children.get(r.id) ?? [])].sort((a, b) => -byStartDesc(a, b))) visit(c, depth + 1);
  };
  for (const r of [...roots].sort(byStartDesc)) visit(r, 0);
  // Runs only reachable through a cycle (never expected): listed flat at the end rather than dropped.
  for (const r of runs) if (!placed.has(r.id)) visit(r, 0);
  return out;
}

export function AgentRunsTable({ projectId }: { projectId: string }) {
  const { t, locale, formatDateTime } = useI18n();
  const [outcome, setOutcome] = useState<"" | AgentRunOutcome>("");
  const [runs, setRuns] = useState<AgentRun[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    listAgentRuns({ project_id: projectId, limit: AGENT_RUNS_LIMIT, ...(outcome ? { outcome } : {}) }, controller.signal)
      .then((page) => setRuns(page.items))
      .catch((e: unknown) => {
        if (!controller.signal.aborted) setError(e);
      });
    return () => controller.abort();
  }, [projectId, outcome, refreshKey]);

  // Agent names are a display nicety: without them a run still shows its profile id.
  useEffect(() => {
    const controller = new AbortController();
    listAgents(controller.signal)
      .then((r) => setNames(new Map(r.items.map((a) => [a.id, a.name]))))
      .catch(() => undefined);
    return () => controller.abort();
  }, []);

  const refresh = useDebouncedCallback(() => setRefreshKey((n) => n + 1), 1500);
  useRealtimeEvents((e) => {
    if (e.type === "run.started" || e.type === "task.transitioned" || e.type === "task.completed") refresh();
  }, projectId);

  const rows = useMemo(() => (runs ? nestRuns(runs) : []), [runs]);
  const byId = useMemo(() => new Map((runs ?? []).map((r) => [r.id, r])), [runs]);
  const agentName = (id: string) => names.get(id) ?? id.slice(0, 8);
  const purposeLabel = (p: AgentRunPurpose) => t(`agentRuns.purpose.${p}` as MessageKey);
  const titleId = `agent-runs-${projectId}-title`;

  return (
    <section className="card agent-runs" aria-labelledby={titleId} data-testid="agent-runs">
      <div className="card-header">
        <h2 id={titleId} className="card-title" style={{ flex: 1 }}>
          {t("agentRuns.title")}
        </h2>
        <Button size="sm" variant="ghost" icon={<RefreshIcon size={14} />} onClick={() => setRefreshKey((n) => n + 1)}>
          {t("common.refresh")}
        </Button>
      </div>
      <div className="card-body stack">
        <p className="field-hint">{t("agentRuns.hint")}</p>
        <div className="runs-toolbar">
          <SelectField label={t("agentRuns.outcomeFilter")} value={outcome} onChange={(e) => setOutcome(e.target.value as "" | AgentRunOutcome)}>
            <option value="">{t("common.all")}</option>
            {AGENT_RUN_OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {t(`agentRuns.outcome.${o}` as MessageKey)}
              </option>
            ))}
          </SelectField>
        </div>
        {error && !runs ? (
          <ErrorBanner error={error} onRetry={() => setRefreshKey((n) => n + 1)} />
        ) : runs === null ? (
          <LoadingBlock label={t("common.loading")} />
        ) : rows.length === 0 ? (
          <EmptyState icon={<CpuIcon />} title={t("agentRuns.emptyTitle")} headingLevel={3}>
            <p>{t("agentRuns.emptyBody")}</p>
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table agent-runs-table">
              <caption className="sr-only">{t("agentRuns.title")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("agentRuns.agent")}</th>
                  <th scope="col">{t("agentRuns.purposeColumn")}</th>
                  <th scope="col">{t("agentRuns.parent")}</th>
                  <th scope="col">{t("agentRuns.status")}</th>
                  <th scope="col" className="num">
                    {t("agentRuns.steps")}
                  </th>
                  <th scope="col" className="num">
                    {t("agentRuns.modelCalls")}
                  </th>
                  <th scope="col" className="num">
                    {t("agentRuns.toolCalls")}
                  </th>
                  <th scope="col" className="num">
                    {t("agentRuns.tokens")}
                  </th>
                  <th scope="col" className="num">
                    {t("agentRuns.cost")}
                  </th>
                  <th scope="col">{t("agentRuns.started")}</th>
                  <th scope="col">{t("agentRuns.finished")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ run, depth }) => {
                  const parent = run.parent_run_id ? byId.get(run.parent_run_id) : undefined;
                  const view = OUTCOME_VIEW[run.outcome];
                  return (
                    <tr key={run.id} data-testid="agent-run-row" data-depth={depth} data-purpose={run.purpose} data-run-id={run.id}>
                      <th scope="row" className="agent-run-agent" style={{ paddingInlineStart: `calc(var(--space-3) + ${depth * 20}px)` }}>
                        {depth > 0 ? (
                          <span className="agent-run-branch" aria-hidden="true">
                            ↳
                          </span>
                        ) : null}
                        <span>{agentName(run.agent_profile_id)}</span>
                      </th>
                      <td>{purposeLabel(run.purpose)}</td>
                      <td>
                        {run.parent_run_id === null ? (
                          <span className="subtle">—</span>
                        ) : parent ? (
                          <span>
                            {agentName(parent.agent_profile_id)} <span className="subtle">· {purposeLabel(parent.purpose)}</span>
                          </span>
                        ) : (
                          <span className="mono subtle">{run.parent_run_id.slice(0, 8)}</span>
                        )}
                      </td>
                      <td>
                        <Pill tone={view.tone} icon={view.icon}>
                          {t(`agentRuns.outcome.${run.outcome}` as MessageKey)}
                        </Pill>
                      </td>
                      <td className="num mono">{formatNumber(run.steps, locale)}</td>
                      <td className="num mono">{formatNumber(run.model_calls, locale)}</td>
                      <td className="num mono">{formatNumber(run.tool_calls, locale)}</td>
                      <td className="num mono">
                        <span title={t("agentRuns.tokensDetail", { input: formatNumber(run.input_tokens, locale), output: formatNumber(run.output_tokens, locale) })}>
                          {formatNumber(run.input_tokens + run.output_tokens, locale)}
                        </span>
                        <span className="sr-only">
                          {" "}
                          ({t("agentRuns.tokensDetail", { input: formatNumber(run.input_tokens, locale), output: formatNumber(run.output_tokens, locale) })})
                        </span>
                      </td>
                      <td className="num mono">{run.cost_estimated_cents === null ? <span className="subtle">{t("agentRuns.unknownCost")}</span> : formatCents(run.cost_estimated_cents, locale)}</td>
                      <td>{formatDateTime(run.started_at)}</td>
                      <td>{run.ended_at ? formatDateTime(run.ended_at) : <span className="subtle">{t("agentRuns.notFinished")}</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {runs && runs.length >= AGENT_RUNS_LIMIT ? <p className="field-hint">{t("agentRuns.limited", { n: AGENT_RUNS_LIMIT })}</p> : null}
      </div>
    </section>
  );
}
